// src/crm/tasks/TasksPage.tsx
import { useState } from 'react'
import { useAppStore } from '../../shell/appStore'
import { useTasks, useCreateTask, usePatchTask, useDeleteTask } from './useTasks'
import { sortTasksByDueDate, filterOverdueTasks } from './taskUtils'
import type { Task, TaskStatus } from '../../lib/types'

const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'À faire',
  doing: 'En cours',
  done: 'Terminé',
}

const STATUS_COLORS: Record<TaskStatus, string> = {
  todo: 'var(--muted)',
  doing: 'var(--bronze)',
  done: '#4a9a5c',
}

const TODAY = new Date().toISOString().slice(0, 10)

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.4375rem 0.625rem',
  background: 'var(--elevated)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3125rem',
  fontSize: '0.8125rem',
  color: 'var(--muted)',
  fontWeight: 500,
}

// ── Modale de création ──────────────────────────────────────────────────────
function CreateTaskDialog({
  appId,
  onClose,
}: {
  appId: string
  onClose: () => void
}) {
  const createTask = useCreateTask(appId)
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    createTask.mutate(
      { appId, title: title.trim(), dueDate: dueDate || null },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 9998,
        }}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-label="Nouvelle tâche"
        style={{
          position: 'fixed',
          top: '20vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(480px, 90vw)',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          zIndex: 9999,
          padding: '1.5rem',
        }}
      >
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--fg)', margin: '0 0 1.25rem' }}>
          Nouvelle tâche
        </h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <label style={labelStyle}>
            <span>Titre *</span>
            <input
              aria-label="Titre"
              type="text"
              required
              placeholder="Ex : Relancer créateur"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={inputStyle}
              autoFocus
            />
          </label>
          <label style={labelStyle}>
            <span>Échéance</span>
            <input
              aria-label="Échéance"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={inputStyle}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={createTask.isPending}
              style={{
                padding: '0.4375rem 1rem',
                background: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                color: 'var(--muted)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!title.trim() || createTask.isPending}
              style={{
                padding: '0.4375rem 1rem',
                background: 'var(--arancio)',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                opacity: createTask.isPending ? 0.6 : 1,
              }}
            >
              {createTask.isPending ? 'Création…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Modale d'édition ────────────────────────────────────────────────────────
function EditTaskDialog({
  task,
  appId,
  onClose,
}: {
  task: Task
  appId: string
  onClose: () => void
}) {
  const patchTask = usePatchTask(appId)
  const [status, setStatus] = useState<TaskStatus>(task.status)
  const [title, setTitle] = useState(task.title)
  const [dueDate, setDueDate] = useState(task.dueDate ?? '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patchTask.mutate(
      { id: task.id, data: { status, title: title.trim() || task.title, dueDate: dueDate || null } },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 9998,
        }}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-label="Modifier la tâche"
        style={{
          position: 'fixed',
          top: '20vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(480px, 90vw)',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          zIndex: 9999,
          padding: '1.5rem',
        }}
      >
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--fg)', margin: '0 0 1.25rem' }}>
          Modifier la tâche
        </h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <label style={labelStyle}>
            <span>Titre</span>
            <input
              aria-label="Titre"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={inputStyle}
              autoFocus
            />
          </label>
          <label style={labelStyle}>
            <span>Statut</span>
            <select
              aria-label="Statut"
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
              style={inputStyle}
            >
              <option value="todo">À faire</option>
              <option value="doing">En cours</option>
              <option value="done">Terminé</option>
            </select>
          </label>
          <label style={labelStyle}>
            <span>Échéance</span>
            <input
              aria-label="Échéance"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={inputStyle}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={patchTask.isPending}
              style={{
                padding: '0.4375rem 1rem',
                background: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                color: 'var(--muted)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={patchTask.isPending}
              style={{
                padding: '0.4375rem 1rem',
                background: 'var(--arancio)',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                opacity: patchTask.isPending ? 0.6 : 1,
              }}
            >
              {patchTask.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Ligne de tâche ──────────────────────────────────────────────────────────
function TaskRow({
  task,
  onEdit,
  onStatusChange,
  onDelete,
}: {
  task: Task
  onEdit: (task: Task) => void
  onStatusChange: (id: string, status: TaskStatus) => void
  onDelete: (id: string) => void
}) {
  const isOverdue =
    task.status !== 'done' && task.dueDate !== null && task.dueDate < TODAY

  return (
    <div
      data-testid={`task-row-${task.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.625rem 1rem',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {/* Statut cyclique */}
      <button
        title={`Statut : ${STATUS_LABELS[task.status]}`}
        onClick={() => {
          const next: TaskStatus =
            task.status === 'todo' ? 'doing' : task.status === 'doing' ? 'done' : 'todo'
          onStatusChange(task.id, next)
        }}
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: `2px solid ${STATUS_COLORS[task.status]}`,
          background: task.status === 'done' ? STATUS_COLORS[task.status] : 'transparent',
          cursor: 'pointer',
          flexShrink: 0,
          padding: 0,
        }}
        aria-label={`Marquer comme ${task.status === 'done' ? 'à faire' : task.status === 'todo' ? 'en cours' : 'terminé'}`}
      />

      {/* Titre + date — cliquable pour ouvrir l'édition */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onEdit(task)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onEdit(task) }}
        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        aria-label={`Modifier la tâche : ${task.title}`}
      >
        <span
          style={{
            fontSize: '0.875rem',
            color: task.status === 'done' ? 'var(--dim)' : 'var(--fg)',
            textDecoration: task.status === 'done' ? 'line-through' : 'none',
          }}
        >
          {task.title}
        </span>
        {task.dueDate && (
          <span
            style={{
              marginLeft: '0.5rem',
              fontSize: '0.75rem',
              color: isOverdue ? '#d9531e' : 'var(--dim)',
              fontFamily: 'var(--mono)',
            }}
          >
            {isOverdue ? '⚠ ' : ''}{task.dueDate}
          </span>
        )}
      </div>

      {/* Badge statut — valeur brute pour les tests */}
      <span
        data-testid="task-status-badge"
        style={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: STATUS_COLORS[task.status],
          minWidth: '60px',
          textAlign: 'right',
        }}
      >
        {task.status}
      </span>

      {/* Supprimer */}
      <button
        onClick={() => onDelete(task.id)}
        title="Supprimer"
        aria-label="Supprimer la tâche"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--dim)',
          cursor: 'pointer',
          fontSize: '1rem',
          padding: '0 0.25rem',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  )
}

// ── Page principale ──────────────────────────────────────────────────────────
export function TasksPage() {
  const appId = useAppStore((s) => s.activeAppId)
  const { data: allTasks = [], isLoading } = useTasks(appId)
  const patchTask = usePatchTask(appId ?? '')
  const deleteTask = useDeleteTask(appId ?? '')

  const [showCreate, setShowCreate] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all')

  const sorted = sortTasksByDueDate(allTasks)
  const overdue = filterOverdueTasks(allTasks, TODAY)

  const displayed =
    filterStatus === 'all' ? sorted : sorted.filter((t) => t.status === filterStatus)

  if (!appId) {
    return (
      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Sélectionnez une app pour voir les tâches.
      </div>
    )
  }

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '860px' }}>
      {showCreate && (
        <CreateTaskDialog appId={appId} onClose={() => setShowCreate(false)} />
      )}
      {editingTask && (
        <EditTaskDialog
          task={editingTask}
          appId={appId}
          onClose={() => setEditingTask(null)}
        />
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.25rem',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>
          Tâches
          {overdue.length > 0 && (
            <span
              style={{
                marginLeft: '0.625rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#d9531e',
                background: 'rgba(217,83,30,0.12)',
                border: '1px solid rgba(217,83,30,0.3)',
                borderRadius: '10px',
                padding: '0.1rem 0.5rem',
              }}
            >
              {overdue.length} en retard
            </span>
          )}
        </h1>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            padding: '0.375rem 0.875rem',
            background: 'var(--arancio)',
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '0.8125rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + Nouvelle tâche
        </button>
      </div>

      {/* Filtres statut */}
      <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.75rem' }}>
        {(['all', 'todo', 'doing', 'done'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            style={{
              padding: '0.25rem 0.625rem',
              background: filterStatus === s ? 'var(--arancio)' : 'var(--surface)',
              border: `1px solid ${filterStatus === s ? 'var(--arancio)' : 'var(--line)'}`,
              borderRadius: '14px',
              color: filterStatus === s ? '#fff' : 'var(--muted)',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {s === 'all' ? 'Toutes' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {/* Liste */}
      {isLoading ? (
        <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement…</div>
      ) : displayed.length === 0 ? (
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--dim)',
            fontSize: '0.875rem',
          }}
        >
          {filterStatus === 'all' ? 'Aucune tâche. Créez la première.' : 'Aucune tâche dans ce statut.'}
        </div>
      ) : (
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {displayed.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onEdit={setEditingTask}
              onStatusChange={(id, status) =>
                patchTask.mutate({ id, data: { status } })
              }
              onDelete={(id) => deleteTask.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
